import { useState, type FormEvent } from "react";
import { submitLead } from "@/lib/submissions";
import { trackEvent } from "@/lib/analytics";
import { ConsentCheckbox, DocReviewCheckbox } from "@/components/consent-checkbox";

interface Props {
  location: string;
}

/** Compact lead form used on programmatic location pages in place of the
 *  standard CTA button. One fewer click for the visitor; the copy above it
 *  stays crawlable. */
export function LocationLeadForm({ location }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [consent, setConsent] = useState(false);
  const [docReview, setDocReview] = useState(false);
  const [state, setState] = useState<"idle" | "submitting" | "done" | "error">("idle");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setState("submitting");
    try {
      await submitLead({
        docReview,
        name,
        email,
        company: company || undefined,
        consent,
        source: `location:${location}`,
        message: `Enquiry from the ${location} location page.`,
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      trackEvent("form_submit", {
        category: "lead",
        action: "location_enquiry",
        label: location,
        properties: { form_type: "location", location },
      });
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <section className="bg-brand px-6 py-16 text-brand-foreground lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-3xl leading-tight sm:text-4xl">
            Thank you, we'll be in touch.
          </h2>
          <p className="mt-3 text-brand-foreground/80">
            One of our senior HR advisers will reply within 2 business hours.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-brand px-6 py-16 text-brand-foreground lg:px-8">
      <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">
            Get started
          </p>
          <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
            Speak to a senior HR expert for your {location} team
          </h2>
          <p className="mt-4 text-brand-foreground/80">
            We provide fixed-fee, insurance-backed HR support with a named adviser. There is no
            obligation, and we will assess your setup and quote in plain English.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          aria-label={`Request a call-back for ${location}`}
          className="rounded-2xl bg-background p-6 text-foreground shadow-xl"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="loc-name" className="text-xs font-medium text-foreground">
                Your name
              </label>
              <input
                id="loc-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full min-h-11 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div>
              <label htmlFor="loc-email" className="text-xs font-medium text-foreground">
                Work email
              </label>
              <input
                id="loc-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full min-h-11 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <div>
              <label htmlFor="loc-company" className="text-xs font-medium text-foreground">
                Company
              </label>
              <input
                id="loc-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-1 w-full min-h-11 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <ConsentCheckbox id="consent-location-lead" checked={consent} onChange={setConsent} />
            <DocReviewCheckbox id="doc-review-location-lead" checked={docReview} onChange={setDocReview} />
            <button
              type="submit"
              disabled={state === "submitting"}
              className="w-full min-h-11 rounded-md bg-accent px-5 py-3 text-sm font-medium text-accent-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {state === "submitting" ? "Sending…" : "Request my call-back"}
            </button>
            {state === "error" && (
              <p role="alert" className="text-xs text-destructive">
                Something went wrong. Please email hello@hrservicecentre.co.uk directly.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              We reply within 2 business hours. Your details are never shared.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
